import { Component } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import tabbable from 'tabbable';
import { genUID } from '@entrata/utils';

// setup for SSR environment (without document global)
const isDom = typeof document !== 'undefined';
const defaultTarget = isDom ? document.body : {};

/** A component for creating modals.
 * `Modal` uses React's `createPortal` method to render a modal
 * in a `div` external to the app's root container.
 * `Modal` handles creating a `div` and rendering the modal in it,
 * as well as cleaning up the extra `div` on unmount.
 * It also handles the accessibility concerns of trapping focus inside the modal
 * while it's open, closing the modal when pressing the *esc* key and providing
 * all necessary aria roles and unique title and description ids via its `render` prop.
 */
class Modal extends Component {
  constructor(props) {
    super(props);
    this.closeOnEsc = e => {
      if (e.keyCode === 27) {
        const {
          onClose
        } = this.props;
        // stop bubbling so we only close the topmost modal
        // keeping the rest of the stack intact
        e.stopPropagation();
        onClose(e);
      }
    };
    this.resetTabbables = () => {
      this.firstTab && this.firstTab.removeEventListener('keydown', this.killTab);
      this.lastTab && this.lastTab.removeEventListener('keydown', this.trapFocus);
    };
    this.trapTabbables = () => {
      const tabs = tabbable(this.container);
      // eslint-disable-next-line prefer-destructuring
      this.firstTab = tabs[0];
      if (tabs.length > 1) {
        this.lastTab = tabs[tabs.length - 1];
        this.firstTab.addEventListener('keydown', this.trapFocus);
        this.lastTab.addEventListener('keydown', this.trapFocus);
      } else if (this.firstTab) {
        this.firstTab.addEventListener('keydown', this.killTab);
      }
    };
    this.killTab = e => {
      if (e.keyCode === 9) e.preventDefault();
    };
    this.trapFocus = e => {
      if (e.keyCode === 9) {
        if (e.target === this.lastTab && !e.shiftKey) {
          e.preventDefault();
          this.firstTab.focus();
        }
        if (e.target === this.firstTab && e.shiftKey) {
          e.preventDefault();
          this.lastTab.focus();
        }
      }
    };
    this.uid = genUID();
    // generate a unique id for accessible titling and description
    this.descriptionId = `modal-${this.uid}-desc`;
    this.titleId = `modal-${this.uid}-title`;

    // get the DOM target
    this.target = props.target || defaultTarget;

    // initialize this.container for SSR
    this.container = {};

    // create portal container with needed attributes
    if (isDom) {
      this.container = document.createElement('div');
      this.container.id = `modal-${this.uid}`;
      this.container.tabIndex = '-1';
      if (props.className) this.container.className = props.className;
      // if any additional portal props are passed, add them
      if (typeof props.containerAttributes === 'object') {
        Object.entries(props.containerAttributes).forEach(_ref => {
          let [key, value] = _ref;
          this.container.setAttribute(key, value);
        });
      }
      // insert the portal container in the right place
      if (props.placement === 'before' && this.target.firstChild) {
        this.target.insertBefore(this.container, this.target.firstChild);
      } else {
        this.target.appendChild(this.container);
      }
    }
  }
  componentDidMount() {
    const {
      timeout
    } = this.props;
    // disable page scrolling
    document.body.style.overflow = 'hidden';
    // trap focus to the modal
    setTimeout(() => {
      this.trapTabbables();
      this.container.addEventListener('keydown', this.closeOnEsc);
      // move focus to the modal
      this.firstTab.focus();
    }, timeout);
  }
  componentDidUpdate() {
    // retrap focus, in case the content changed
    this.resetTabbables();
    this.trapTabbables();
  }
  componentWillUnmount() {
    // reenable page scrolling
    document.body.style.overflow = '';
    // cleanup listeners before killing the portal
    this.container && this.container.removeEventListener('keydown', this.closeOnEsc);
    this.resetTabbables();
    this.target.removeChild(this.container);
  }
  render() {
    const {
      children,
      render
    } = this.props;
    return /*#__PURE__*/createPortal((children || render)({
      containerProps: {
        role: 'dialog',
        'aria-labelledby': this.titleId,
        'aria-describedby': this.descriptionId,
        'aria-modal': 'true'
      },
      descriptionId: this.descriptionId,
      titleId: this.titleId
    }), this.container);
  }
}
Modal.defaultProps = {
  placement: 'after',
  timeout: 100
};
Modal.propTypes = {
  /** Children as a render function. */
  children: PropTypes.func,
  /** A classname to add to the modal container added by the portal */
  className: PropTypes.string,
  /** Any additional html attributes that need to end up on the portal div.  */
  containerAttributes: PropTypes.object,
  /** Function to run when closing the modal */
  onClose: PropTypes.func.isRequired,
  /** Determines whether to insert the portal container as the first or last element in the target.
   * Options are 'after' or 'before. */
  placement: PropTypes.oneOf(['after', 'before']),
  /** Render function. */
  render: PropTypes.func,
  /** Determines in which DOM element to add the portal container.
   * This must be a DOM node. Defaults to document.body. */
  target: PropTypes.object,
  /** For animated modals, pass the animation duration to keep focus behavior in sync. */
  timeout: PropTypes.number
};
export { Modal };